import { useState, useMemo, useEffect } from 'react';
import { X, Package } from '@phosphor-icons/react';
import { fmt } from '@/lib/api';
import { fmtDec, pf } from '@/lib/numFmt';

/**
 * VariantMatrixModal — grid-view om in 1 keer meerdere (kleur × maat) hoeveelheden
 * in te geven voor een artikel met varianten. Genereert 1 documentregel per niet-lege cel.
 *
 * Props:
 *  - artikel: { id, omschrijving, foto_url, dimensies, varianten, prijzen, btw }
 *  - open: boolean
 *  - onClose: () => void
 *  - onSubmit: (lines) => void  -- array van {combo_key, combo, variant, aantal}
 *  - prijsGroep?: string
 */
export default function VariantMatrixModal({ artikel, open, onClose, onSubmit, prijsGroep = 'basic', targetLineIsFilled = false }) {
  const dims = artikel?.dimensies || [];
  const vars = artikel?.varianten || [];
  const [quantities, setQuantities] = useState({}); // {combo_key: qty}

  useEffect(() => {
    if (open) setQuantities({});
  }, [open, artikel?.id]);

  // We ondersteunen tot 2D-grid (bv. kleur × maat). Bij 3 dimensies:
  // toont eerste 2 als grid + extra dropdown voor de 3e dimensie (niet nodig voor nu → tonen per cel)
  const rowDim = dims[0] || null; // bv. kleur
  const colDim = dims[1] || null; // bv. maat
  const extraDim = dims[2] || null;

  // Samengevoegde waardes per dim
  const rowValues = rowDim ? (rowDim.waardes || []) : [];
  const colValues = colDim ? (colDim.waardes || []) : [];

  // Lookup: combo_key → variant
  const variantByKey = useMemo(() => {
    const m = {};
    vars.forEach(v => { if (v.combo_key) m[v.combo_key] = v; });
    return m;
  }, [vars]);

  // Bouw combo_key in zelfde format als backend: "dim1=waarde|dim2=waarde|..."
  const buildKey = (rowVal, colVal, extraVal = null) => {
    const parts = [];
    if (rowDim && rowVal != null) parts.push(`${rowDim.naam}=${rowVal}`);
    if (colDim && colVal != null) parts.push(`${colDim.naam}=${colVal}`);
    if (extraDim && extraVal != null) parts.push(`${extraDim.naam}=${extraVal}`);
    return parts.join('|');
  };

  const updateQty = (key, val) => {
    setQuantities(p => {
      const next = { ...p };
      const v = (val ?? '').toString().trim();
      if (!v || pf(v) <= 0) delete next[key]; else next[key] = v;
      return next;
    });
  };

  // Totalen per rij en kolom
  const rowTotals = useMemo(() => {
    const m = {};
    for (const r of rowValues) {
      let s = 0;
      for (const c of colValues) {
        const k = buildKey(r, c);
        s += pf(quantities[k] || 0);
      }
      m[r] = s;
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantities, rowValues, colValues]);

  const colTotals = useMemo(() => {
    const m = {};
    for (const c of colValues) {
      let s = 0;
      for (const r of rowValues) {
        const k = buildKey(r, c);
        s += pf(quantities[k] || 0);
      }
      m[c] = s;
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantities, rowValues, colValues]);

  const grandTotal = useMemo(() => {
    return Object.values(quantities).reduce((s, v) => s + pf(v), 0);
  }, [quantities]);

  const basisPrijs = (artikel?.prijzen || {})[prijsGroep] || 0;

  // Schat totaal € op basis van aantal × basisprijs (override negeren voor snelheid)
  const geschatBedrag = useMemo(() => {
    let s = 0;
    for (const [k, q] of Object.entries(quantities)) {
      const v = variantByKey[k];
      const prijs = (v && v.prijs_override != null) ? parseFloat(v.prijs_override) : basisPrijs;
      s += pf(q) * prijs;
    }
    return s;
  }, [quantities, variantByKey, basisPrijs]);

  const handleSubmit = () => {
    const lines = [];
    for (const [combo_key, qty] of Object.entries(quantities)) {
      const q = pf(qty);
      if (q <= 0) continue;
      const variant = variantByKey[combo_key];
      if (!variant) continue;
      lines.push({
        combo_key,
        combo: variant.combo,
        variant,
        aantal: q,
      });
    }
    if (!lines.length) {
      return;
    }
    onSubmit?.(lines);
  };

  if (!open || !artikel) return null;

  // 3e dimensie (breedte bv.) — als aanwezig, nemen we eerste waarde als default
  const extraValue = extraDim ? (extraDim.waardes || [])[0] : null;

  const fotoSrc = artikel.foto_url
    ? (artikel.foto_url.startsWith('http') ? artikel.foto_url : `${process.env.REACT_APP_BACKEND_URL}${artikel.foto_url}`)
    : null;

  // Alleen grid tonen als we minstens 2 dimensies hebben
  const kanMatrix = rowDim && colDim && rowValues.length && colValues.length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(8px)' }}
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
      data-testid="variant-matrix-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-4 border-b border-purple-100 flex gap-3 items-center">
          {fotoSrc ? (
            <img src={fotoSrc} alt="" className="w-16 h-16 object-cover rounded-lg border-2 border-white shadow-sm flex-shrink-0" />
          ) : (
            <div className="w-16 h-16 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center text-2xl flex-shrink-0">
              <Package size={28} className="text-gray-400" weight="duotone" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wider text-purple-700 font-bold mb-0.5 inline-flex items-center gap-1">
              🔲 Matrix-invoer
            </div>
            <div className="text-base font-bold text-gray-900 truncate">{artikel.omschrijving}</div>
            <div className="text-[11px] text-gray-500 font-mono">{artikel.art_code || ''} · vanaf € {basisPrijs.toFixed(2)}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 self-start" data-testid="var-matrix-close">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 overflow-auto flex-1">
          {/* Mode-notice: wat gebeurt er na bevestigen? */}
          <div className={`mb-3 px-3 py-2 text-xs rounded border ${
            targetLineIsFilled
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-blue-50 border-blue-200 text-blue-800'
          }`} data-testid="var-matrix-mode-notice">
            {targetLineIsFilled ? (
              <><b>✓ Toevoegen-modus</b>: nieuwe regels worden ONDER de huidige regel ingevoegd. Je bestaande regel blijft behouden.</>
            ) : (
              <><b>↻ Vervangen-modus</b>: deze lege regel wordt vervangen door de matrix-invoer.</>
            )}
          </div>
          {!kanMatrix ? (
            <div className="text-center text-gray-500 text-sm py-8">
              Dit artikel heeft te weinig dimensies voor matrix-invoer. Gebruik de gewone variant-picker.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="text-xs border-collapse" data-testid="var-matrix-grid">
                <thead>
                  <tr>
                    <th className="sticky left-0 bg-gray-50 border border-gray-200 px-3 py-2 text-left text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
                      {rowDim.label || rowDim.naam} ↓ &nbsp; {colDim.label || colDim.naam} →
                    </th>
                    {colValues.map(c => (
                      <th key={c} className="border border-gray-200 px-2 py-2 text-center font-semibold text-gray-700 min-w-[60px]">
                        {c}
                      </th>
                    ))}
                    <th className="border border-gray-200 bg-indigo-50 px-3 py-2 text-center font-bold text-indigo-800 min-w-[60px]">
                      Totaal
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rowValues.map(r => (
                    <tr key={r}>
                      <td className="sticky left-0 bg-gray-50 border border-gray-200 px-3 py-1.5 font-semibold text-gray-700 whitespace-nowrap">
                        {r}
                      </td>
                      {colValues.map(c => {
                        const k = buildKey(r, c, extraValue);
                        const variant = variantByKey[k];
                        const stock = variant ? (variant.voorraad ?? 0) : 0;
                        const val = quantities[k] || '';
                        const qty = pf(val);
                        const exceedsStock = variant && qty > stock;
                        return (
                          <td key={c} className={`border border-gray-200 p-0 ${!variant ? 'bg-gray-100' : ''}`}>
                            {variant ? (
                              <div className="flex flex-col">
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={fmtDec(val || '')}
                                  onChange={e => updateQty(k, e.target.value.replace(',', '.'))}
                                  className={`w-full text-center text-sm px-1 py-1 border-0 outline-none focus:bg-yellow-50 ${exceedsStock ? 'text-amber-700 bg-amber-50' : qty > 0 ? 'text-blue-700 bg-blue-50 font-bold' : 'text-gray-900'}`}
                                  placeholder="-"
                                  data-testid={`var-matrix-cell-${r}-${c}`}
                                />
                                <div className="text-[9px] text-center text-gray-400 pb-0.5 select-none">
                                  stock: <span className={stock > 0 ? 'text-emerald-600' : 'text-red-500'}>{stock}</span>
                                </div>
                              </div>
                            ) : (
                              <div className="text-center text-gray-300 text-xs py-2 select-none">—</div>
                            )}
                          </td>
                        );
                      })}
                      <td className="border border-gray-200 bg-indigo-50 px-3 py-1.5 text-center font-bold text-indigo-800">
                        {rowTotals[r] > 0 ? rowTotals[r] : ''}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td className="sticky left-0 bg-indigo-100 border border-gray-200 px-3 py-2 text-left font-bold text-indigo-900">
                      Totaal
                    </td>
                    {colValues.map(c => (
                      <td key={c} className="bg-indigo-50 border border-gray-200 px-2 py-2 text-center font-bold text-indigo-800">
                        {colTotals[c] > 0 ? colTotals[c] : ''}
                      </td>
                    ))}
                    <td className="bg-indigo-600 border border-indigo-700 px-3 py-2 text-center font-bold text-white text-sm" data-testid="var-matrix-grand-total">
                      {grandTotal}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Hulp-tekst */}
              <div className="mt-3 text-[11px] text-gray-500 leading-relaxed">
                💡 Vul per cel de gewenste hoeveelheid in. Elke cel wordt een aparte documentregel met de correcte variant-code en prijs.
                Cellen met <span className="text-gray-400">—</span> bestaan niet (geen variant voor deze combinatie).
                Velden met amber-kleur overschrijden de huidige voorraad (waarschuwing, blokkeert niet).
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex gap-2 justify-between items-center">
          <div className="text-sm text-gray-600">
            <span className="font-bold text-indigo-700">{grandTotal}</span> stuks in <span className="font-bold">{Object.keys(quantities).length}</span> combinatie(s)
            {grandTotal > 0 && (
              <span className="ml-3 text-gray-500">≈ {fmt(geschatBedrag)}</span>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700 px-3" data-testid="var-matrix-annuleer">
              Annuleren
            </button>
            <button
              onClick={handleSubmit}
              disabled={grandTotal <= 0}
              className="btn btn-primary btn-sm"
              data-testid="var-matrix-bevestig"
            >
              {grandTotal > 0 ? `✓ ${Object.keys(quantities).length} regel(s) toevoegen` : 'Vul minstens 1 cel in'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
